import { Injectable, signal } from '@angular/core';
import { DefaultErrorModalComponent } from '@monorepo/ui';
import { FeatureService } from '@monorepo/services';
import { TypedModalController } from '@monorepo/utils';
import { FactoryConfigFacade, TAutoPlanSource } from '../../../../redux-store';
import {
  AUTO_PLAN_ORDER_MODAL_ID,
  AutoPlanOrderModalComponent,
  TAutoPlanOrderModalComponentDismissEmitVal,
  TAutoPlanOrderModalComponentProps,
} from './auto-plan-order-modal.component';

export type TAutoPlanSelectableOpl = {
  opl_id: string;
  factory_id: string;
  status?: string;
  userLoadQty?: number;
  order: { max_load_qty: string };
};

@Injectable({ providedIn: 'root' })
export class AutoPlanOrderService {
  readonly hasAutoLineLoadingPermission = signal(
    this.featureService.isFeatureAvailable('PT', 'AUTOMATIC_LINE_LOADING'),
  );

  constructor(
    private typedModalController: TypedModalController,
    private featureService: FeatureService,
    private factoryConfigFacade: FactoryConfigFacade,
  ) {}

  async openAutoPlanModal(selectedOpls: TAutoPlanSelectableOpl[], source: TAutoPlanSource): Promise<boolean> {
    // The cap is per account; without it the backend still enforces the limit, so the check is skipped.
    const autoPlanConfig = await this.factoryConfigFacade.loadAutoPlanConfig();
    const selectionError = this.#getSelectionError(selectedOpls, autoPlanConfig?.max_opls_per_run);
    if (selectionError) {
      await this.#presentErrorModal(selectionError);
      return false;
    }

    const modal = await this.typedModalController.createAndPresent<
      TAutoPlanOrderModalComponentProps,
      TAutoPlanOrderModalComponentDismissEmitVal
    >({
      component: AutoPlanOrderModalComponent,
      id: AUTO_PLAN_ORDER_MODAL_ID,
      componentProps: {
        oplDetails: {
          oplRequests: selectedOpls.map((opl) => ({
            oplId: opl.opl_id,
            factoryId: opl.factory_id,
            quantity: opl.userLoadQty ?? Number(opl.order.max_load_qty),
          })),
        },
        source,
      },
      cssClass: 'plani8-modal__auto-plan-modal',
    });

    const { role } = await modal.onDidDismiss();
    return role === 'SUCCESS';
  }

  // OPLs may come from any number of OTLs and factories.
  #getSelectionError(selectedOpls: TAutoPlanSelectableOpl[], maxOplsPerRun: number | undefined): string | null {
    if (selectedOpls.length === 0) return 'Please select at least one OPL to Auto Plan.';
    if (maxOplsPerRun && selectedOpls.length > maxOplsPerRun)
      return `A maximum of ${maxOplsPerRun} OPLs can be auto planned at once.`;
    if (selectedOpls.some((opl) => opl.status === 'COMPLETELY_PLANNED'))
      return 'Completely planned OPLs cannot be auto planned. Please deselect them and try again.';

    const oplFactoryKeys = new Set(selectedOpls.map((opl) => `${opl.opl_id}|${opl.factory_id}`));
    if (oplFactoryKeys.size !== selectedOpls.length) return 'Each OPL can be selected only once per factory.';

    return null;
  }

  async #presentErrorModal(errorTxt: string) {
    await this.typedModalController.createAndPresent<{ errorTxt: string }>({
      component: DefaultErrorModalComponent,
      componentProps: { errorTxt },
      cssClass: 'm-modal__default-error-modal',
    });
  }
}
